import React, { FC, useEffect, useRef, useState } from 'react';
import { InView as ReactIntersectionObserver, IntersectionObserverProps } from 'react-intersection-observer';

import { useBreakpoints } from 'hooks/useBreakpoints';
import { usePrevious } from 'hooks/usePrevious';
import { withSidebarContext } from '../SidebarContext/WithSidebarContext';

type Props = Pick<IntersectionObserverProps, Exclude<keyof IntersectionObserverProps, 'onChange'>> & {
  openSidebar: () => void;
  closeSidebar: () => void;
  disableSidebarWindowEvents: () => void;
  enableSidebarWindowEvents: () => void;
  ignoreTriggerTill: number;
};

const VerticalMenuTrigger: FC<Props> = ({ children: fn, ...props }) => {
  const [triggerCount, setTriggerCount] = useState(0);
  const inViewRef = useRef(false);
  const { browser } = useBreakpoints();
  const isMobile = browser.is.mobile;
  const isPrevMobile = usePrevious(isMobile);

  function onChange(inView: boolean) {
    setTriggerCount(triggerCount + 1);

    if (triggerCount < props.ignoreTriggerTill) return;

    if (inView) {
      props.disableSidebarWindowEvents();
    } else {
      props.enableSidebarWindowEvents();
    }

    if (isMobile) return;

    if (inView) {
      props.openSidebar();
    } else {
      props.closeSidebar();
    }
    inViewRef.current = inView;
  }

  useEffect(() => {
    if (!!isPrevMobile === isMobile) return;

    const inView = inViewRef.current;

    if (inView) {
      props.disableSidebarWindowEvents();
    } else {
      props.enableSidebarWindowEvents();
      return;
    }

    if (!isMobile) {
      props.openSidebar();
    } else {
      props.closeSidebar();
    }
  }, [isMobile]);

  useEffect(() => {
    if (isMobile) props.closeSidebar();

    return () => {
      props.enableSidebarWindowEvents();
    };
  }, []);

  return (
    <ReactIntersectionObserver onChange={onChange} {...props}>
      {props => fn(props)}
    </ReactIntersectionObserver>
  );
};

const SidebarContexted = withSidebarContext(({ open, close, enableWindowEvents, disableWindowEvents }) => ({
  openSidebar: open,
  closeSidebar: close,
  enableSidebarWindowEvents: enableWindowEvents,
  disableSidebarWindowEvents: disableWindowEvents,
}))(VerticalMenuTrigger);
type SidebarContexted = typeof SidebarContexted;

export {
  SidebarContexted as VerticalMenuTrigger,
  VerticalMenuTrigger as _VerticalMenuTrigger,
  Props as _VerticalMenuTriggerProps,
};
